import React from "react";
import TextMaskingCropping from "./TextMaskingCropping";
import { connect } from "react-redux";
import {
  isCroppingSelector,
  contextSelector,
  selectedItemsSelector,
  zoomSelector,
  designDataSelector,
  elementPreviewsSelector
} from "state/ui/editorContext/editorContextSelectors";
import { getPath } from "lib/lodash";
import { getPageOffsetInCanvasbackground } from "views/components/Editor/utils";
import { update as updateEditorContextState } from "state/ui/editorContext/editorContextActions";
import { setInnerImageRotation } from "state/ui/editor/innerImageRotationUtils";
import { Logger } from "lib";

export class TextMaskingCroppingContainer extends React.Component {
  constructor(props) {
    super(props);

    this.getElementWithPreview = this.getElementWithPreview.bind(this);
    this.getPageOffsetInCanvasbackground = this.getPageOffsetInCanvasbackground.bind(
      this
    );
    this.getElementWithPreview = this.getElementWithPreview.bind(this);
    this.getElementPreview = this.getElementPreview.bind(this);
    this.startTextMaskImageCropMoving = this.startTextMaskImageCropMoving.bind(
      this
    );
    this.finishTextMaskImageCropMoving = this.finishTextMaskImageCropMoving.bind(
      this
    );
    this.handleInnerImageRotationPreview = this.handleInnerImageRotationPreview.bind(
      this
    );
    this.handleInnerImageRotationCommit = this.handleInnerImageRotationCommit.bind(
      this
    );
    this.pendingPreviewRotation = null;
    this.previewRafId = null;
  }

  componentWillUnmount() {
    if (this.previewRafId !== null) {
      cancelAnimationFrame(this.previewRafId);
      this.previewRafId = null;
    }
  }

  // Coalesce preview ticks to one redux dispatch per animation frame; the
  // commit path bypasses rAF so the final value is never a frame stale.
  // Mirrors GridCellCroppingContainer / PhotoFrameCroppingContainer.
  handleInnerImageRotationPreview(rotation) {
    this.pendingPreviewRotation = rotation;
    if (this.previewRafId !== null) return;
    this.previewRafId = requestAnimationFrame(() => {
      this.previewRafId = null;
      const next = this.pendingPreviewRotation;
      this.pendingPreviewRotation = null;
      if (next === null || next === undefined) return;
      this.handleInnerImageRotationCommit(next);
    });
  }

  handleInnerImageRotationCommit(rotation) {
    if (this.previewRafId !== null) {
      cancelAnimationFrame(this.previewRafId);
      this.previewRafId = null;
    }
    this.pendingPreviewRotation = null;

    const { selectedItems, designData, updateContext } = this.props;
    const selectedItem = selectedItems[0];
    if (!selectedItem) return;

    const element = designData.getElement(selectedItem.itemId);
    if (!element) return;

    const existingPreview = selectedItem.preview || {};
    const baseMaskImage = existingPreview.maskImage || element.maskImage || {};

    // Route through setInnerImageRotation so any future caller emitting
    // NaN / out-of-range / negative angles lands a normalized value, matching
    // the Stage 1 (PhotoFrame) and Stage 2 (GridCell) commit paths.
    const { maskImage: nextMaskImage } = setInnerImageRotation(
      { ...element, maskImage: baseMaskImage },
      null,
      rotation
    );

    const updatedSelectedItems = selectedItems.map((item, index) =>
      index === 0
        ? {
            ...item,
            preview: {
              ...existingPreview,
              maskImage: nextMaskImage
            }
          }
        : item
    );

    updateContext({ selectedItems: updatedSelectedItems });
  }

  getElementPreview(elementId) {
    if (!elementId) return {};
    const { elementPreviews, selectedItems } = this.props;

    const elementSelection =
      selectedItems.find(item => item.itemId === elementId) || {};

    return {
      ...elementPreviews[elementId],
      ...elementSelection.preview
    };
  }

  getElementWithPreview(elementId) {
    if (!elementId) return {};
    const { designData } = this.props;

    const element = designData.getElement(elementId);

    const elementPreview = this.getElementPreview(elementId);

    // when the preview is image instructions we want to replace without removing unchanged ones
    if (Object.keys(elementPreview).includes("imageInstructions")) {
      const previewInstructions = elementPreview.imageInstructions;
      const elementInstructions = element.imageInstructions;

      const originalInstructionsByDomId = elementInstructions.reduce(
        (combinedInstructions, currentInstruction) => ({
          ...combinedInstructions,
          [currentInstruction.domId]: currentInstruction
        }),
        {}
      );

      previewInstructions.forEach(previewInstruction => {
        originalInstructionsByDomId[
          previewInstruction.domId
        ] = previewInstruction;
      });

      elementPreview.imageInstructions = Object.values(
        originalInstructionsByDomId
      );
    }

    return {
      ...element,
      ...elementPreview
    };
  }

  getPageOffsetInCanvasbackground(pageId) {
    if (!pageId) return 0;
    return getPageOffsetInCanvasbackground(pageId);
  }

  startTextMaskImageCropMoving() {
    Logger.info(
      "TextMaskingCroppingContainer.startTextMaskImageCropMoving called"
    );

    this.props.updateContext({
      isCropping: true
    });
  }

  finishTextMaskImageCropMoving({ differenceFromInitialOffset }) {
    Logger.info(
      "TextMaskingCroppingContainer.finishTextMaskImageCropMoving called"
    );

    const { zoom, designData, selectedItems } = this.props;
    const selectedTextMaskImage = getPath(this.props, "selectedItems.0");

    let textElement = designData.getElement(selectedItems[0].itemId);

    textElement = {
      ...textElement,
      ...selectedItems[0].preview
    };

    const maskImage = {
      ...textElement.maskImage,
      left: textElement.maskImage.left + differenceFromInitialOffset.x / zoom,
      top: textElement.maskImage.top + differenceFromInitialOffset.y / zoom
    };

    if (maskImage.top > 0) {
      maskImage.top = 0;
    }

    const maskImageBottom = maskImage.top + maskImage.height;
    if (maskImageBottom < textElement.height) {
      maskImage.top += textElement.height - maskImageBottom;
    }

    if (maskImage.left > 0) {
      maskImage.left = 0;
    }

    const maskImageRight = maskImage.left + maskImage.width;
    if (maskImageRight < textElement.width) {
      maskImage.left += textElement.width - maskImageRight;
    }

    const selectedItemUpdated = [
      {
        ...selectedTextMaskImage,
        preview: {
          ...selectedTextMaskImage.preview,
          maskImage
        }
      }
    ];

    this.props.updateContext({
      isCropping: false,
      selectedItems: selectedItemUpdated
    });
  }

  render() {
    if (
      !this.props.canvasBackground ||
      !this.props.context ||
      !this.props.context.isCroppingTextMaskImage
    ) {
      return null;
    }

    const selectedTextMaskImage = getPath(this.props, "selectedItems.0");

    const textElement = this.getElementWithPreview(
      selectedTextMaskImage?.itemId
    );

    const pageId = selectedTextMaskImage?.pageId || "";
    const pageOffset = this.getPageOffsetInCanvasbackground(pageId);

    return (
      <TextMaskingCropping
        {...this.props}
        textElement={textElement}
        pageId={pageId}
        pageOffset={pageOffset}
        startCropMoving={this.startTextMaskImageCropMoving}
        finishCropMoving={this.finishTextMaskImageCropMoving}
        onInnerImageRotationPreview={this.handleInnerImageRotationPreview}
        onInnerImageRotationCommit={this.handleInnerImageRotationCommit}
      />
    );
  }
}

const mapStateToProps = state => {
  return {
    isCropping: isCroppingSelector(state),
    context: contextSelector(state),
    selectedItems: selectedItemsSelector(state),
    zoom: zoomSelector(state),
    elementPreviews: elementPreviewsSelector(state),
    designData: designDataSelector(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateContext: args => dispatch(updateEditorContextState(args))
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TextMaskingCroppingContainer);
